import PropTypes from "prop-types"
import React from "react"
import { components } from "react-select"

import downIcon from "../../../../assets/Hub_Icons/downchevron.svg"
import downIconDark from "../../../../assets/Hub_Icons/downchevron_dark.svg"
import close from "../../../../assets/common/close.svg"
import closeDark from "../../../../assets/common/close_dark.svg"
import upIcon from "../../../../assets/Hub_Icons/upchevron.svg"
import upIconDark from "../../../../assets/Hub_Icons/upchevron_dark.svg"
import { useDarkMode } from "../../../../context/DarkModeContext"

const { ValueContainer, Placeholder } = components

const DropdownIndicatorComponent = (props) => {
    const { isDarkMode } = useDarkMode()
    
    return (
        <components.DropdownIndicator { ...props }>
            <img
                alt="dropdown"
                src={ 
                    props.selectProps.menuIsOpen 
                        ? (isDarkMode ? upIconDark : upIcon)
                        : (isDarkMode ? downIconDark : downIcon)
                }
                className=""
                height={ "16px" }
                width={ "16px" }
                onError={ (e) => {
                    // Fallback to regular icon if dark mode icon fails
                    e.target.src = props.selectProps.menuIsOpen ? upIcon : downIcon
                } }
                onLoad={ (e) => {
                    console.log('HelperComponents - Icon loaded successfully:', e.target.src)
                } }
            />
        </components.DropdownIndicator>
    )
}

const ClearIndicatorComponent = (props) => {
    const { isDarkMode } = useDarkMode()
    return (
        <components.ClearIndicator { ...props }>
            <img
                alt="close"
                src={ isDarkMode ? closeDark : close }
                className=""
                height={ "16px" }
                width={ "16px" }
                onError={ (e) => {
                    console.error('Close icon failed to load:', e.target.src)
                    // Fallback to regular close icon if dark mode icon fails
                    e.target.src = close
                } }
            />
        </components.ClearIndicator>
    )
}

const InputComponent = (props) => {
    if (!props.selectProps.menuIsOpen || props.selectProps.menuIsOpen) {
        return (
            <components.Input autoFocus={ props.selectProps.menuIsOpen } { ...props }>
                { props.children }
            </components.Input>
        )
    }
    else {
        return (
            <div style={ { border: "1px dotted gray" } }>
                <components.Input autoFocus={ props.selectProps.menuIsOpen } { ...props }>
                    { props.children }
                </components.Input>
            </div>
        )
    }
}

const CHECK_BOX_SELECTOR = `checkmark absolute h-[18px] w-[18px]  bg-white dark:bg-gray-700 border rounded-[4px] 
                                    border-primary-50 dark:border-gray-500  z-[2]  after:absolute after:hiddent after:content-[''] 
                                    after:w-[6px] after:h-[12px] after:rotate-45  after:border-b-[2px]  
                                    after:border-r-[2px] after:border-white after:left-[5px] after:top-[1px]`

const OptionsComponent = (props,isAllOption,isSmallInputBox) => {
    let allSelected = (isAllOption && 
        props?.selectProps?.value?.length === props?.selectProps?.options?.length - 1)
    return (
        <components.Option { ...props }>
            <div className="flex items-center gap-1" >
                <div className="relative cursor-pointer  select-none w-[18px] h-[18px] custom-checkbox">
                    <input
                        className="peer w-[18px] h-[18px] absolute opacity-0 z-[1]"
                        type="checkbox"
                        checked={ (props.value === "*" && allSelected) ? props.isSelected : null }
                        onChange={ () => null }
                    />
                    <span
                        className={ ` ${CHECK_BOX_SELECTOR} ${props.isSelected
                                        || (allSelected) ? "after:block !bg-primary-50 dark:!bg-blue-600" : ""}` }
                    ></span>
                </div>
                <label className={ `dark:text-white ${isSmallInputBox && props?.label?.length > 35 ?
                    "pl-2" : props?.label?.length > 50 ? "pl-2" : ""}` }>
                    { props.label }
                </label>
            </div>
        </components.Option>
    )
}

const MoreSelectedBadgeComponent = ({ items, isDisabled = false }) => {
    const title = items?.join(", ")
    return (
        <div
            className={ isDisabled ? 
                "text-primary-50  font-bold ml-3 absolute right-5 dark:text-darkModeBlue" :
            "text-primary-50 font-bold ml-3 absolute right-0 mt-[5px] dark:text-darkModeBlue" }
            title={ title }
        >
            { `+ ${items.length}` }
        </div>
    )
}

const MultiValueComponent = ({ index, getValue, isDisabled,selectedItemLength, ...props }) => {
    const maxToShow = selectedItemLength || 3
    const overflow = getValue()
        .slice(maxToShow)
        .map((x) => x.label)
    if (index < maxToShow) {
        return <components.MultiValue { ...props } />
    }
    if (index === maxToShow) {
        return <MoreSelectedBadgeComponent items={ overflow } isDisabled={ isDisabled } />
    }
}

const CustomValueContainerComponent = ({ children,isRequired,labelIsRequired, ...props }) => {
    const PLACEHOLDER_CLASS = `${props.selectProps.isFocused || props.hasValue
        ? "block bg-primary text-xxs w-max"
        : "block bg-primary text-sm "
    } `

    return (
        <ValueContainer { ...props }>
            { labelIsRequired && <Placeholder { ...props } isFocused={ props.isFocused }>
                <span className="flex h-[2px] relative justify-center items-center dark:bg-gray-800">
                    <span className={ PLACEHOLDER_CLASS }>
                        { isRequired && <i className="text-red mr-1">*</i> }
                        <span className="dark:text-gray-400">{ props.selectProps.placeholder }</span>
                    </span>
                </span>
            </Placeholder> }
            { React.Children.map(children, (child) =>
                child && child.key !== "placeholder" ? child : null
            ) }
        </ValueContainer>
    )
}

DropdownIndicatorComponent.propTypes = {
    selectProps: PropTypes.shape({
        menuIsOpen: PropTypes.bool.isRequired 
    }).isRequired
}

ClearIndicatorComponent.propTypes = {
    selectProps: PropTypes.object.isRequired 
}

InputComponent.propTypes = {
    children: PropTypes.node, 
    selectProps: PropTypes.shape({
        menuIsOpen: PropTypes.bool.isRequired 
    }).isRequired
}

OptionsComponent.propTypes = {
    isAllOption: PropTypes.bool, 
    isSelected: PropTypes.bool, 
    isSmallInputBox: PropTypes.bool, 
    label: PropTypes.string, 
    selectProps: PropTypes.shape({
        options: PropTypes.array, 
        value: PropTypes.array 
    }).isRequired,
    value: PropTypes.string 
}

MoreSelectedBadgeComponent.propTypes = {
    isDisabled: PropTypes.bool, 
    items: PropTypes.array.isRequired 
}

MultiValueComponent.propTypes = {
    getValue: PropTypes.func.isRequired, 
    index: PropTypes.number.isRequired, 
    isDisabled: PropTypes.bool, 
    selectedItemLength: PropTypes.number 
}

CustomValueContainerComponent.propTypes = {
    children: PropTypes.node, 
    hasValue: PropTypes.bool, 
    isFocused: PropTypes.bool,
    isRequired: PropTypes.bool,
    labelIsRequired: PropTypes.bool,
    selectProps: PropTypes.shape({
        isFocused: PropTypes.bool,
        placeholder: PropTypes.string 
    }).isRequired
}

export {
    ClearIndicatorComponent,
    CustomValueContainerComponent,
    DropdownIndicatorComponent,
    InputComponent,
    MoreSelectedBadgeComponent,
    MultiValueComponent,
    OptionsComponent }
